import { useListArtifacts } from '@/hooks/queries/artifacts/use-list-artifacts';

import type { ChatArtifactCardModel } from '@/components/chat-artifact-cards';

/**
 * Promotion happens after the run reports completion, so the deliverables are
 * not in the table the moment the card turns terminal. Poll briefly, then stop
 * — a run whose terminal node produces nothing never gets rows, and this must
 * not become a request that outlives the conversation.
 */
const POLL_MS = 3_000;
const MAX_POLLS = 10;

/**
 * Whether the deliverables poll should fire again.
 *
 * dataUpdateCount only counts successful responses -- bounding on it alone
 * means a failing listArtifacts call never trips MAX_POLLS and polls
 * forever. errorUpdateCount covers the rest of the attempts.
 */
export function shouldKeepPolling(state: {
  dataUpdateCount: number;
  errorUpdateCount: number;
}): boolean {
  return state.dataUpdateCount + state.errorUpdateCount < MAX_POLLS;
}

/**
 * The artifacts a finished run produced, as cards.
 *
 * A run's outputs never reach the chat stream: the turn that launched it ends
 * as soon as the agent has replied, minutes before the workflow produces
 * anything, so there is no turn left to carry an artifact frame. The run's own
 * deliverables are instead read back from the artifact table by source run.
 */
export function useRunArtifactCards(
  runId: string,
  {
    enabled,
    isHistorical = false,
  }: { enabled: boolean; isHistorical?: boolean },
): ChatArtifactCardModel[] {
  const { data, isLoading } = useListArtifacts(
    { source_run_id: runId, sort: 'updated_at_asc' },
    {
      enabled,
      refetchInterval: (query) => {
        if (query.state.data?.artifacts?.length) return false;
        return shouldKeepPolling(query.state) ? POLL_MS : false;
      },
    },
  );

  // `isLoading` is the first response only -- a live run polling with nothing
  // yet gets no placeholder, since there the output really does arrive late.
  if (isHistorical && isLoading) {
    return [{ key: `${runId}-deliverables`, item: null }];
  }

  return (data?.artifacts ?? []).map((artifact) => ({
    key: artifact.id,
    item: artifact,
    artifactId: artifact.id,
  }));
}
